import React, { useEffect, useMemo, useState } from "react";
import { COUNTY_COUNT, LISTINGS, STATEWIDE_NOTICE_COUNTIES, equity, fmtMoney } from "./landingData";

// ParcelMap — hero "stylized parcel view" (ported from Claude-design 51d58388).
// SVG parcel-grid + roads + metro block, with 3 clickable pins (real listings).
// Clicking a pin swaps the closable property card; radar pulse under each pin;
// live per-listing countdown. Fixed visual — no pan/zoom/scroll.

interface PinView {
  key: string; left: string; top: string;
  addr: string; meta: string; assessed: string; owed: string; bid: string; eq: string;
  offset: number; // seconds until the (rolling) upset window closes
}

const fm = (n: number) => (n > 0 ? fmtMoney(n) : "—");

const PINS: PinView[] = [
  { key: "meadowdale", left: "33%", top: "46%", addr: LISTINGS[0].address, meta: `Charlotte, NC · PIN ${LISTINGS[0].parcel}`, assessed: fm(LISTINGS[0].assessed), owed: fm(LISTINGS[0].taxesOwed), bid: fm(LISTINGS[0].current), eq: fm(equity(LISTINGS[0])), offset: 6 * 86400 + 14 * 3600 + 22 * 60 },
  { key: "albemarle", left: "62%", top: "34%", addr: LISTINGS[1].address, meta: `Charlotte, NC · PIN ${LISTINGS[1].parcel}`, assessed: fm(LISTINGS[1].assessed), owed: fm(LISTINGS[1].taxesOwed), bid: fm(LISTINGS[1].current), eq: fm(equity(LISTINGS[1])), offset: 3 * 86400 + 9 * 3600 + 41 * 60 },
  { key: "madrid", left: "46%", top: "64%", addr: LISTINGS[2].address, meta: `Charlotte, NC · PIN ${LISTINGS[2].parcel}`, assessed: fm(LISTINGS[2].assessed), owed: fm(LISTINGS[2].taxesOwed), bid: fm(LISTINGS[2].current), eq: fm(equity(LISTINGS[2])), offset: 8 * 86400 + 2 * 3600 + 15 * 60 },
];

const ParcelMap: React.FC = () => {
  const [sel, setSel] = useState(0);
  const [showCard, setShowCard] = useState(true);
  const p = PINS[sel];

  // Countdown: rolling target reset whenever the selected pin changes.
  const target = useMemo(() => Date.now() + p.offset * 1000, [p.offset]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  let s = Math.max(0, Math.floor((target - now) / 1000));
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const countdown = `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;

  const pick = (i: number) => { setSel(i); setShowCard(true); };

  return (
    <div className="lp-map">
      <svg className="lp-map-svg" viewBox="0 0 700 500" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="lp-parcels" width="46" height="46" patternUnits="userSpaceOnUse">
            <rect width="46" height="46" fill="none" stroke="#EBEEF2" strokeWidth="1" />
          </pattern>
          <pattern id="lp-parcels2" width="46" height="46" patternUnits="userSpaceOnUse" patternTransform="translate(23 23)">
            <rect width="23" height="23" fill="none" stroke="#EFF2F5" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="700" height="500" fill="#FCFDFE" />
        <rect width="700" height="500" fill="url(#lp-parcels)" />
        <rect width="700" height="500" fill="url(#lp-parcels2)" />
        <path d="M180 120 L360 90 L470 200 L420 340 L230 360 L150 250 Z" fill="#EAF0F6" opacity="0.6" />
        <path d="M-20 360 C 160 320, 320 380, 720 300" fill="none" stroke="#D8DEE6" strokeWidth="3" />
        <path d="M120 -20 C 200 180, 300 260, 360 540" fill="none" stroke="#D8DEE6" strokeWidth="3" />
        <path d="M-20 160 C 200 140, 420 200, 720 150" fill="none" stroke="#E3E7EC" strokeWidth="2" />
        <path d="M520 -20 C 480 160, 540 320, 480 540" fill="none" stroke="#E3E7EC" strokeWidth="2" />
      </svg>

      <div className="lp-map-chip">Deep parcel detail in {COUNTY_COUNT} counties · notices statewide (all {STATEWIDE_NOTICE_COUNTIES})</div>

      {PINS.map((pin, i) => (
        <button
          key={pin.key}
          className={"lp-pin" + (i === sel ? "" : " inactive")}
          style={{ left: pin.left, top: pin.top }}
          onClick={() => pick(i)}
          aria-label={pin.addr}
          type="button"
        >
          <span className="lp-radar" />
          {i === sel && <span className="lp-radar b" />}
          <span className="lp-dot" />
        </button>
      ))}

      {showCard && (
        <div className="lp-propcard">
          <div className="lp-propcard-head">
            <div>
              <p className="lp-propcard-addr">{p.addr}</p>
              <p className="lp-propcard-meta">{p.meta}</p>
            </div>
            <button className="lp-close" onClick={() => setShowCard(false)} aria-label="Close" type="button">×</button>
          </div>
          <dl>
            <div><dt>Assessed value</dt><dd className="lp-tnum">{p.assessed}</dd></div>
            <div><dt>Taxes owed</dt><dd className="lp-tnum">{p.owed}</dd></div>
            <div><dt>Current bid</dt><dd className="lp-tnum">{p.bid}</dd></div>
            <div className="eqrow"><dt>Est. equity</dt><dd className="lp-tnum">{p.eq}</dd></div>
          </dl>
          <div className="lp-propcard-foot">
            <span className="lab">Upset bid ends</span>
            <span className="clk">{countdown}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParcelMap;
